import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthenticationService,
  Authority,
  AuthorizationService,
} from '@pwc-bei/security';
import { EAccessPlanVariable } from 'src/app/enum/access-plan-variable';
import { RoutePath } from 'src/app/enums/route-path.enum';
import { ETermsAndConditonDocumentType } from 'src/app/enums/terms-and-conditions-type';
import { AuthenticationUtilsService } from 'src/app/services/authentication-utils.service';
import { TermsAndConditionService } from 'src/app/services/terms-and-condition.service';
import { environment } from '../../../environments/environment';
import { BankCode } from '../../enums/bank-code.enum';
import { SocietiesService, User, UserTypeEnum } from '@pwc-bei/common';
import { AccessPlanType } from 'src/app/enums/access-plan.enum';
import { ESharedDocumentType } from 'src/app/enums/shared-document-type';
import { SharedDocumentUploadService } from 'src/app/services/shared-document/shared-document-upload.service';
import { of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { piAlert, PwcIconsLibrary } from '@pwc-bei/icons';

@Component({
    selector: 'app-specific-terms-conditions',
    templateUrl: './specific-terms-conditions.component.html',
    styleUrls: ['./specific-terms-conditions.component.scss'],
    standalone: false
})
export class SpecificTermsConditionsComponent implements OnInit {
  constructor(
    private authenticationUtilsService: AuthenticationUtilsService,
    private termsAndConditionService: TermsAndConditionService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
    private sharedDocumentUploadService: SharedDocumentUploadService,
    private societyService: SocietiesService,
    private translateService: TranslateService,
    private pwcIconsLibrary: PwcIconsLibrary
  ) { }

  user: User;
  termsAndConditionsForm: FormGroup;
  isLr: boolean = false;
  isUserSmart: boolean = false;

  tcId: string;
  tcAccessPlan: AccessPlanType;

  showPage: boolean = false;

  buttonAcceptEnabled: boolean = false;

  generalConditionDownloaded: boolean = false;
  orderFormDownloaded: boolean = false;
  readonly directChannel = environment.directChannel;

  societyId: number;

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.tcId = params.termsID; // Access the 'id' parameter from the URL
    });

    this.pwcIconsLibrary.registerIcons([
      piAlert
    ]);

    this.authenticationService.dataUser().subscribe((user) => {
      this.isLr = user.userType === UserTypeEnum.LEGAL_REPRESENTATIVE;
      if (!this.isLr) {
        this.orderFormDownloaded = true;
      }
      this.user = user;
    });

    this.societyService.currentSociety$.pipe(
      switchMap(soc=>{
        if(soc == null || soc == undefined) {
          return this.societyService.getSelectedSociety()
        }
        return of(soc);
      })).subscribe(society => {
        if(society != null && society != undefined) {
          this.societyId = society.id;
        }
    });


    this.termsAndConditionsForm = this.formBuilder.group({
      tec: [false, [Validators.required]],
      clausole: [false, [Validators.required]],
      privacy: [false],
    });

    this.termsAndConditionsForm.controls.tec.disable();
    this.termsAndConditionsForm.controls.clausole.disable();
    this.termsAndConditionsForm.controls.privacy.disable();

    this.termsAndConditionService
      .retrieveSpecificCondition(this.tcId)
      .subscribe((res) => {
        if (res.tec) {
          this.navigateToUserProfile();
        } else {
          this.showPage = true;
          this.tcAccessPlan = res.accessPlan;
          if(res.accessPlan === AccessPlanType.SMART){
            this.isUserSmart = true;
            this.orderFormDownloaded = this.orderFormDownloaded || this.isUserSmart;
          }
        }
      });

    if (!this.directChannel) {
      this.orderFormDownloaded = true;
    }
  }

  /**
   * Method to accept: Terms and Conditions, Privacy
   */
  acceptConditions(): void {
    if(!this.checkMandatoryConditions()) {
      return;
    }
    // SET BUTTON ACCEPT TO NOT ENABLED WHEN THE BUTTON IS PRESSED
    this.buttonAcceptEnabled = true;
    this.termsAndConditionService
      .acceptSpecificTermsAndConditions(
        this.tcId,
        this.tcAccessPlan,
        this.termsAndConditionsForm.get('tec').value,
        this.termsAndConditionsForm.get('clausole').value,
        this.termsAndConditionsForm.get('privacy').value,
        this.societyId
      )
      .subscribe(
        {
          next: (res) => {
            this.authenticationUtilsService.setSessionVariable(
              'terms-conditions-accepted',
              'true',
            );
            // operating user
            if (
              this.user &&
              this.user.userType.localeCompare('OPERATING_USER') == 0
            ) {
              this.navigateToHome();
            } else {
              this.navigateToUserProfile();
            }
          },
          error: (err) => {
            this.buttonAcceptEnabled = false;
            console.log('err', err);
          },
        }
      );
  }

  navigateToUserProfile(): void {
    this.router.navigate([RoutePath.USER_PROFILE]);
  }

  navigateToHome(): void {
    this.router.navigate([RoutePath.HOME]);
  }

  /**
   *
   */
  rejectConditions(): void {
    // TODO: Add message for the user
    this.navigateToHome();
  }

  /**
   * Method to download General Conditions (Point&Click)
   */
  downloadGeneralConditions(): void {
    this.sharedDocumentUploadService.downloadDoc(ESharedDocumentType.GENERAL_CONDITIONS);

    this.generalConditionDownloaded = true;

    if (this.generalConditionDownloaded && this.orderFormDownloaded) {
      this.enableCheckBoxes();
    }
  }

  /**
   * Method to download General Conditions (Point&Click)
   */
  downloadOrderForm(): void {
    this.sharedDocumentUploadService.downloadDoc(this.getUserPlan())
    this.orderFormDownloaded=true;
    if(this.generalConditionDownloaded && this.orderFormDownloaded){
      this.enableCheckBoxes();
    }
  }

  enableCheckBoxes(): void {
    this.termsAndConditionsForm.controls.tec.enable();
    if (this.termsAndConditionsForm.controls.tec.value === null) {
      this.termsAndConditionsForm.controls.tec.setValue(false);
    }
    this.termsAndConditionsForm.controls.clausole.enable();
    if (this.termsAndConditionsForm.controls.clausole.value === null) {
      this.termsAndConditionsForm.controls.clausole.setValue(false);
    }

    this.termsAndConditionsForm.controls.privacy.enable();
    if (this.termsAndConditionsForm.controls.privacy.value === null) {
      this.termsAndConditionsForm.controls.privacy.setValue(false);
    }
  }

  /**
   * Method to download Privacy (Point&Click)
   */
  downloadPrivacy(): void {
    this.sharedDocumentUploadService.downloadDoc(ESharedDocumentType.PRIVACY);
  }

  /**
   * Method to check if the user has already accepted: Terms and Conditions and/or Privacy
   */
  checkMandatoryConditions(): Boolean {
    if (
      this.termsAndConditionsForm.get('tec').value == true &&
      this.termsAndConditionsForm.get('clausole').value == true
    ) {
      this.buttonAcceptEnabled = true;
    }
    return (
      this.termsAndConditionsForm.get('tec').value &&
      this.termsAndConditionsForm.get('clausole').value
    );
  }

  getControlFormValue(control: string): Boolean {
    return this.termsAndConditionsForm.get(control).value;
  }

  getUserPlan(): ESharedDocumentType {
    if (environment.accessPlanEnabled && this.tcAccessPlan){
      switch (this.tcAccessPlan) {
        case AccessPlanType.SMART:
          return ESharedDocumentType.ORDER_FORM_SMART;
        case AccessPlanType.PLUS:
          return ESharedDocumentType.ORDER_FORM_PLUS;
        case AccessPlanType.PREMIUM:
          return ESharedDocumentType.ORDER_FORM_PREMIUM;
        default:
          return ESharedDocumentType.ORDER_FORM;
      }
    }
    return ESharedDocumentType.ORDER_FORM;
  }
}
